import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card } from '@saas-pages/shared/ui'
import { startToolOperation, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import { useLocale, useT } from '@/shared/i18n'
import {
  buildMarkdownExport,
  acceptMarkdownSource,
} from '@/shared/markdown/sourceExport'
import { buildStandaloneHtml } from '@/shared/markdown/htmlExport'
import {
  createMarkdownAnalyticsSession,
  type MarkdownAnalyticsAttempt,
} from '@/shared/markdown/analyticsSession'
import {
  createBrowserMarkdownWorker,
  createMarkdownPreviewController,
  type MarkdownPreviewController,
} from '@/shared/markdown/previewWorker'
import {
  MAX_MARKDOWN_PREVIEW_BYTES,
  MAX_MARKDOWN_SOURCE_BYTES,
} from '@/shared/markdown/limits'
import type { MarkdownRenderResult } from '@/shared/markdown/renderMarkdown'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

const ANALYTICS_CONTEXT = {
  app: 'devdeck',
  domain: 'devdeck.ru',
  route: '/markdown-editor',
  utility: 'markdown-editor',
  processingMode: 'local',
} satisfies ToolAnalyticsContext

const EMPTY_RESULT: MarkdownRenderResult = {
  html: '',
  stats: { words: 0, headings: 0, links: 0, tables: 0 },
  hasRawHtml: false,
  hasImages: false,
}

const PREVIEW_DEBOUNCE_MS = 150

function downloadText(contents: string, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function MarkdownEditorPage() {
  const t = useT()
  const { locale } = useLocale()
  const [markdown, setMarkdown] = useState('')
  const [result, setResult] = useState<MarkdownRenderResult>(EMPTY_RESULT)
  const [sourceLimitError, setSourceLimitError] = useState(false)
  const [previewError, setPreviewError] = useState<'too-large' | 'processing' | null>(null)
  const [previewPending, setPreviewPending] = useState(false)
  const [copied, setCopied] = useState<'html' | 'markdown' | null>(null)
  const copyTimerRef = useRef<number | null>(null)
  const copyGenerationRef = useRef(0)
  const previewTimerRef = useRef<number | null>(null)
  const analyticsSessionRef = useRef<ReturnType<typeof createMarkdownAnalyticsSession> | null>(null)
  const analyticsAttemptRef = useRef<MarkdownAnalyticsAttempt | null>(null)
  const analyticsStartedAtRef = useRef(0)
  const previewControllerRef = useRef<MarkdownPreviewController | null>(null)

  useEffect(() => {
    analyticsSessionRef.current = createMarkdownAnalyticsSession(
      () => startToolOperation(ANALYTICS_CONTEXT),
    )
    previewControllerRef.current = createMarkdownPreviewController(
      createBrowserMarkdownWorker,
      {
        success(nextResult) {
          setResult(nextResult)
          setPreviewError(null)
          setPreviewPending(false)
          analyticsAttemptRef.current?.success(performance.now() - analyticsStartedAtRef.current)
          analyticsAttemptRef.current = null
          analyticsStartedAtRef.current = 0
        },
        error(code) {
          setResult(EMPTY_RESULT)
          setPreviewError(code === 'size_limit' ? 'too-large' : 'processing')
          setPreviewPending(false)
          analyticsAttemptRef.current?.error(code)
          analyticsAttemptRef.current = null
          analyticsStartedAtRef.current = 0
        },
      },
    )
    return () => {
      copyGenerationRef.current += 1
      analyticsSessionRef.current?.dispose()
      analyticsSessionRef.current = null
      analyticsAttemptRef.current = null
      analyticsStartedAtRef.current = 0
      previewControllerRef.current?.dispose()
      previewControllerRef.current = null
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current)
      if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current)
    }
  }, [])

  const showCopied = useCallback((kind: 'html' | 'markdown') => {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current)
    setCopied(kind)
    copyTimerRef.current = window.setTimeout(() => {
      setCopied((current) => current === kind ? null : current)
      copyTimerRef.current = null
    }, 1500)
  }, [])

  const handleChange = useCallback((value: string) => {
    copyGenerationRef.current += 1
    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = null
    }
    setCopied(null)
    if (!acceptMarkdownSource(value)) {
      setSourceLimitError(true)
      const newAttempt = analyticsAttemptRef.current
        ? null
        : analyticsSessionRef.current?.begin(true) ?? null
      if (newAttempt) analyticsStartedAtRef.current = performance.now()
      const attempt = analyticsAttemptRef.current ?? newAttempt
      analyticsAttemptRef.current = attempt
      attempt?.error('size_limit')
      analyticsAttemptRef.current = null
      analyticsStartedAtRef.current = 0
      return
    }

    setMarkdown(value)
    setSourceLimitError(false)
    previewControllerRef.current?.cancel()
    if (previewTimerRef.current !== null) {
      window.clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
    if (!value.trim()) {
      setResult(EMPTY_RESULT)
      setPreviewError(null)
      setPreviewPending(false)
      return
    }

    if (!analyticsAttemptRef.current) {
      analyticsAttemptRef.current = analyticsSessionRef.current?.begin(true) ?? null
      if (analyticsAttemptRef.current) analyticsStartedAtRef.current = performance.now()
    }
    setResult(EMPTY_RESULT)
    setPreviewError(null)
    setPreviewPending(true)
    previewTimerRef.current = window.setTimeout(() => {
      previewTimerRef.current = null
      previewControllerRef.current?.render(value)
    }, PREVIEW_DEBOUNCE_MS)
  }, [])

  const handleClear = useCallback(() => {
    copyGenerationRef.current += 1
    setMarkdown('')
    setResult(EMPTY_RESULT)
    setSourceLimitError(false)
    setPreviewError(null)
    setPreviewPending(false)
    previewControllerRef.current?.cancel()
    if (previewTimerRef.current !== null) {
      window.clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = null
    }
    setCopied(null)
  }, [])

  const handleCopyHtml = useCallback(async () => {
    if (!result.html) return
    const copyGeneration = copyGenerationRef.current
    try {
      await navigator.clipboard.writeText(result.html)
      if (copyGeneration !== copyGenerationRef.current) return
      showCopied('html')
    } catch {
      if (copyGeneration !== copyGenerationRef.current) return
      setCopied(null)
    }
  }, [result.html, showCopied])

  const handleCopyMarkdown = useCallback(async () => {
    if (!markdown) return
    const copyGeneration = copyGenerationRef.current
    try {
      await navigator.clipboard.writeText(buildMarkdownExport(markdown))
      if (copyGeneration !== copyGenerationRef.current) return
      showCopied('markdown')
    } catch {
      if (copyGeneration !== copyGenerationRef.current) return
      setCopied(null)
    }
  }, [markdown, showCopied])

  const handleDownloadHtml = useCallback(() => {
    if (!result.html) return
    downloadText(
      buildStandaloneHtml(result.html, t.markdownEditor.exportTitle, locale),
      'document.html',
      'text/html;charset=utf-8',
    )
  }, [locale, result.html, t.markdownEditor.exportTitle])

  const handleDownloadMarkdown = useCallback(() => {
    if (!markdown) return
    downloadText(buildMarkdownExport(markdown), 'document.md', 'text/markdown;charset=utf-8')
  }, [markdown])

  return (
    <ToolPageShell toolKey="markdownEditor" width="wide">
      <section className="space-y-4">
        <div className="rounded-xl border bg-card/90 p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={handleCopyMarkdown} className={!markdown ? 'hidden' : undefined}>
              {copied === 'markdown' ? t.markdownEditor.copied : t.markdownEditor.copyMarkdown}
            </Button>
            <Button variant="outline" onClick={handleCopyHtml} className={!result.html ? 'hidden' : undefined}>
              {copied === 'html' ? t.markdownEditor.copied : t.markdownEditor.copyHtml}
            </Button>
            <Button variant="secondary" onClick={handleDownloadMarkdown} className={!markdown ? 'hidden' : undefined}>
              {t.markdownEditor.downloadMarkdown}
            </Button>
            <Button variant="secondary" onClick={handleDownloadHtml} className={!result.html ? 'hidden' : undefined}>
              {t.markdownEditor.downloadHtml}
            </Button>
            <Button variant="ghost" onClick={handleClear} className={!markdown ? 'hidden' : undefined}>
              {t.markdownEditor.clear}
            </Button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{t.markdownEditor.flavorNote}</p>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <Card className="flex min-h-0 flex-col overflow-hidden shadow-sm">
            <div className="flex min-h-14 items-center border-b bg-muted/45 px-4 py-3 text-sm font-medium">
              {t.markdownEditor.inputTitle}
            </div>
            <textarea
              value={markdown}
              onChange={(event) => handleChange(event.target.value)}
              placeholder={t.markdownEditor.inputPlaceholder}
              aria-label={t.markdownEditor.inputAriaLabel}
              className="min-h-[420px] w-full flex-1 resize-y border-0 bg-card p-5 font-mono text-[13px] leading-5 text-foreground placeholder:text-muted-foreground/55 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35 lg:min-h-[560px]"
              spellCheck={false}
            />
          </Card>

          <Card className="flex min-h-0 flex-col overflow-hidden shadow-sm">
            <div className="flex min-h-14 items-center border-b bg-muted/45 px-4 py-3 text-sm font-medium">
              {t.markdownEditor.previewTitle}
            </div>
            <div className="min-h-[420px] flex-1 overflow-auto bg-card p-5 lg:min-h-[560px]">
              {previewPending ? (
                <p className="text-sm text-muted-foreground">{t.markdownEditor.previewPending}</p>
              ) : result.html ? (
                <article
                  className="prose prose-slate max-w-none break-words dark:prose-invert prose-a:text-primary prose-code:break-words prose-table:block prose-table:overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: result.html }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t.markdownEditor.previewPlaceholder}</p>
              )}
            </div>
            {result.hasRawHtml && (
              <p className="border-t border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100" role="status">
                {t.markdownEditor.rawHtmlWarning}
              </p>
            )}
            {result.hasImages && (
              <p className="border-t border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100" role="status">
                {t.markdownEditor.imageWarning}
              </p>
            )}
            {sourceLimitError && (
              <p className="border-t bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                {t.markdownEditor.sourceTooLarge.replace('{limit}', String(MAX_MARKDOWN_SOURCE_BYTES / 1024))}
              </p>
            )}
            {previewError !== null && (
              <p className="border-t bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                {previewError === 'too-large'
                  ? t.markdownEditor.previewTooLarge.replace('{limit}', String(MAX_MARKDOWN_PREVIEW_BYTES / 1024))
                  : t.markdownEditor.renderError}
              </p>
            )}
            <div className="flex flex-wrap gap-x-5 gap-y-1 border-t bg-muted/25 px-4 py-2 text-xs text-muted-foreground">
              <span>{t.markdownEditor.words}: {result.stats.words}</span>
              <span>{t.markdownEditor.headings}: {result.stats.headings}</span>
              <span>{t.markdownEditor.links}: {result.stats.links}</span>
              <span>{t.markdownEditor.tables}: {result.stats.tables}</span>
            </div>
          </Card>
        </div>
      </section>
    </ToolPageShell>
  )
}
